<template>
  <section class="learning-suite">
    <div class="section-header">
      <div class="section-heading">
        <span class="section-icon"><v-icon>mdi-book-open-page-variant-outline</v-icon></span>
        <div>
          <div class="section-eyebrow">Dein Lernpfad</div>
          <h2>Learning Suite</h2>
          <p>Arbeite dich Schritt für Schritt durch deine Module und schalte die nächste Lizenzstufe frei.</p>
        </div>
      </div>
    </div>

    <div class="tier-switch" role="tablist" aria-label="Lizenzstufe wählen">
      <button
        v-for="(tier, index) in tierOptions"
        :key="tier.key"
        type="button"
        role="tab"
        class="tier-button"
        :class="{ active: tierTab === index }"
        :style="{ '--tier-color': tier.color, '--tier-background': tier.background }"
        :aria-selected="tierTab === index ? 'true' : 'false'"
        @click="tierTab = index">
        <v-icon>{{ tier.icon }}</v-icon>
        <span>{{ tier.label }}</span>
        <v-icon v-if="!isTierAccessible(tier.key)" class="tier-lock">mdi-lock-outline</v-icon>
      </button>
    </div>

    <div v-if="loading" class="module-grid" aria-label="Module werden geladen">
      <v-skeleton-loader v-for="index in 4" :key="index" dark type="image, article" class="module-skeleton" />
    </div>

    <div v-else-if="visibleModules.length" class="module-grid">
      <v-card
        v-for="moduleItem in visibleModules"
        :key="moduleItem._id"
        dark
        flat
        class="module-card"
        :class="{
          locked: !isTierAccessible(activeTier),
          completed: getModuleProgress(moduleItem._id) === 100,
        }"
        role="button"
        tabindex="0"
        :aria-disabled="!isTierAccessible(activeTier) ? 'true' : 'false'"
        @click="openModule(moduleItem, activeTier)"
        @keydown.enter="openModule(moduleItem, activeTier)"
        @keydown.space.prevent="openModule(moduleItem, activeTier)">
        <div class="module-card-top">
          <span class="module-icon" :style="{ '--tier-color': activeTierOption.color, '--tier-background': activeTierOption.background }">
            <v-icon>{{ moduleItem.icon || "mdi-school-outline" }}</v-icon>
          </span>
          <span class="tier-pill" :style="{ '--tier-color': activeTierOption.color, '--tier-background': activeTierOption.background }">
            {{ activeTierOption.label }}
          </span>
          <span v-if="getModuleProgress(moduleItem._id) === 100" class="complete-mark" title="Abgeschlossen">
            <v-icon>mdi-check</v-icon>
          </span>
        </div>

        <div class="module-copy">
          <h3>{{ moduleItem.title }}</h3>
          <p>{{ moduleItem.description || "Zu diesem Modul ist noch keine Beschreibung hinterlegt." }}</p>
        </div>

        <div class="module-facts">
          <span><v-icon>mdi-play-circle-outline</v-icon>{{ (moduleItem.lessons && moduleItem.lessons.length) || 0 }} Lektionen</span>
          <span><v-icon>mdi-clock-outline</v-icon>{{ moduleItem.estimatedMinutes || 0 }} Min</span>
        </div>

        <div v-if="isTierAccessible(activeTier)" class="progress-block">
          <div class="progress-copy">
            <span>Fortschritt</span>
            <strong>{{ getModuleProgress(moduleItem._id) }}%</strong>
          </div>
          <v-progress-linear
            :value="getModuleProgress(moduleItem._id)"
            color="#009640"
            background-color="rgba(255,255,255,0.08)"
            height="7"
            rounded />
        </div>

        <div v-else class="locked-note">
          <v-icon>mdi-lock-outline</v-icon>
          <span>Vorherige Stufe zuerst abschliessen</span>
        </div>
      </v-card>
    </div>

    <div v-else class="empty-surface">
      <span class="empty-icon"><v-icon>mdi-bookshelf</v-icon></span>
      <h3>Noch keine Module in {{ activeTierOption.label }}</h3>
      <p>Sobald neue Inhalte freigeschaltet sind, findest du sie direkt hier.</p>
    </div>
  </section>
</template>

<script>
import api from "@/api"

export default {
  name: "LearningSuite",
  data() {
    return {
      tierTab: 0,
      modules: [],
      progressEntries: [],
      myLicenses: [],
      loading: true,
      tierOptions: [
        { key: "bronze", label: "Bronze", icon: "mdi-medal-outline", color: "#d89557", background: "rgba(205, 127, 50, 0.14)" },
        { key: "silber", label: "Silber", icon: "mdi-medal-outline", color: "#d4d4d8", background: "rgba(192, 192, 192, 0.12)" },
        { key: "gold", label: "Gold", icon: "mdi-medal", color: "#f4c95d", background: "rgba(255, 215, 0, 0.12)" },
      ],
    }
  },
  computed: {
    activeTierOption() {
      return this.tierOptions[this.tierTab] || this.tierOptions[0]
    },
    activeTier() {
      return this.activeTierOption.key
    },
    visibleModules() {
      return this.modules
        .filter((moduleItem) => moduleItem.tier === this.activeTier && moduleItem.active !== false)
        .sort((first, second) => (first.order || 0) - (second.order || 0))
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [modules, progressEntries, licenses] = await Promise.all([
          api.akademie.fetchModules(),
          api.akademie.fetchProgress(),
          api.akademie.fetchMyLicenses(),
        ])
        this.modules = modules || []
        this.progressEntries = progressEntries || []
        this.myLicenses = licenses || []
      } catch (error) {
        console.error("Fehler beim Laden der Learning Suite Daten:", error)
      } finally {
        this.loading = false
      }
    },
    isTierAccessible(tier) {
      if (tier === "bronze") return true
      if (tier === "silber") return this.myLicenses.some((license) => license.tier === "bronze" && license.status === "active")
      if (tier === "gold") return this.myLicenses.some((license) => license.tier === "silber" && license.status === "active")
      return false
    },
    getModuleProgress(moduleId) {
      const progress = this.progressEntries.find(
        (entry) => entry.module === moduleId || (entry.module && entry.module._id === moduleId),
      )
      if (!progress) return 0
      if (progress.completedAt) return 100
      const moduleItem = this.modules.find((entry) => entry._id === moduleId)
      if (!moduleItem || !moduleItem.lessons) return 0
      const completedLessons = progress.lessons ? progress.lessons.filter((lesson) => lesson.completedAt != null).length : 0
      return Math.floor((completedLessons / (moduleItem.lessons.length + 1)) * 100)
    },
    openModule(moduleItem, tier) {
      if (!this.isTierAccessible(tier)) return
      this.$router.push(`/akademie/modules/${moduleItem._id}`)
    },
  },
}
</script>

<style scoped>
.learning-suite {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 30rem;
  padding: 0.375rem 0 2rem;
  font-size: 1rem;
}

.section-header {
  margin-bottom: 1rem;
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.075), transparent 52%), rgba(255, 255, 255, 0.03);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}

.section-heading > div {
  min-width: 0;
}

.section-icon,
.empty-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.section-icon {
  width: 2.875rem;
  height: 2.875rem;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 0.875rem;
}

.section-icon .v-icon,
.empty-icon .v-icon {
  color: currentColor;
}

.section-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.5rem;
  line-height: 1.25;
}

.section-heading p {
  max-width: 47rem;
  margin: 0.375rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.55;
}

.tier-switch {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.375rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.tier-button {
  display: flex;
  min-height: 2.875rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  outline: none;
  color: #a3a3a8;
  background: transparent;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 750;
  transition: border-color 160ms ease, color 160ms ease, background-color 160ms ease;
}

.tier-button .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.tier-button.active {
  border-color: var(--tier-color);
  color: var(--tier-color);
  background: var(--tier-background);
}

.tier-button:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.tier-lock {
  margin-left: 0.125rem;
  font-size: 1rem !important;
  opacity: 0.75;
}

.module-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.875rem;
}

.module-skeleton,
.module-card {
  min-height: 19rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem !important;
  overflow: hidden;
  background: var(--surface) !important;
}

.module-card {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  outline: none;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.module-card.locked {
  cursor: not-allowed;
  opacity: 0.78;
}

.module-card.completed {
  border-color: rgba(0, 150, 64, 0.32);
}

.module-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.module-card-top {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.module-icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  color: var(--tier-color);
  background: var(--tier-background);
}

.module-icon .v-icon {
  color: currentColor;
}

.tier-pill {
  min-height: 1.75rem;
  padding: 0.3125rem 0.625rem;
  border-radius: 999px;
  color: var(--tier-color);
  background: var(--tier-background);
  font-size: 0.75rem;
  font-weight: 800;
}

.complete-mark {
  display: grid;
  width: 1.875rem;
  height: 1.875rem;
  margin-left: auto;
  place-items: center;
  border-radius: 50%;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.15);
}

.complete-mark .v-icon {
  color: currentColor;
  font-size: 1.125rem;
}

.module-copy {
  flex: 1 1 auto;
  margin-top: 1rem;
}

.module-copy h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.35;
}

.module-copy p {
  display: -webkit-box;
  margin: 0.5rem 0 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.module-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.875rem;
  margin: 1rem 0;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.module-facts span {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #c7c7cb;
  font-size: 0.75rem;
}

.module-facts .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.progress-block {
  margin-top: auto;
}

.progress-copy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.progress-copy strong {
  color: #7ee5a6;
  font-size: 0.875rem;
}

.locked-note {
  display: flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  padding: 0.625rem 0.75rem;
  border-radius: 0.75rem;
  color: #b8b8bc;
  background: rgba(255, 255, 255, 0.045);
  font-size: 0.75rem;
  font-weight: 700;
}

.locked-note .v-icon {
  color: #8f9095;
  font-size: 1.125rem;
}

.empty-surface {
  display: flex;
  min-height: 18rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  background: var(--surface);
  text-align: center;
}

.empty-icon {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  border-radius: 1rem;
}

.empty-surface h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
}

.empty-surface p {
  margin: 0.375rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
}

@media (hover: hover) and (pointer: fine) {
  .module-card:hover:not(.locked) {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }
}

@media (max-width: 78.9375rem) {
  .module-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .learning-suite {
    padding-bottom: 1rem;
  }

  .section-header {
    padding: 1rem;
    border-radius: 1.0625rem;
  }

  .section-heading {
    gap: 0.75rem;
  }

  .section-icon {
    width: 2.625rem;
    height: 2.625rem;
  }

  .section-heading h2 {
    font-size: 1.25rem;
  }

  .tier-switch {
    display: flex;
    overflow-x: auto;
    overscroll-behavior-x: contain;
  }

  .tier-button {
    flex: 1 0 7.5rem;
  }

  .module-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .module-card {
    min-height: 17.5rem;
  }
}
</style>
